import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { createContext, FC, ReactNode, useEffect, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import 'reflect-metadata';

import { MFErrorText } from '~/components/reuseable/MFErrorText';
import { AuthAccessType } from '~/modules/auth/types/AuthTypes';
import { i18n } from '~/modules/lang/LangAdapter';
import { useRootStore } from '~base/hooks/useRootStore';
import { INotification } from '~base/types/BaseTypes';
import { Loader } from '~base/ui/Loader';
import { LangTypeEnum } from '~modules/lang/types/Lang';

import Notification from '../../base/ui/Notification';

export type MFBaseContextPropsType = {
  // Base
  tokens: AuthAccessType | null;
  onLogout: () => void;
  lang: string;
  notification?: INotification;
};

export type MFBaseProviderPropsType = MFBaseContextPropsType & {
  children: ReactNode;
};

export const MFBaseContext = createContext<MFBaseContextPropsType | undefined>(undefined);

// Типы не передаются в родительские проекты, поэтому определяем обязательные пропы
const requiredProps: Partial<keyof MFBaseContextPropsType>[] = ['tokens'];

export const MFBaseProvider: FC<MFBaseProviderPropsType> = observer((props) => {
  const { authStore, langStore, init } = useRootStore();

  const [loading, setLoading] = useState(true);

  const { t } = useTranslation();

  // Effects

  useEffect(
    () => {
      if (props.tokens?.accessToken && props.tokens?.refreshToken) {
        void authStore.setAuthTokens(props.tokens.accessToken, props.tokens.refreshToken);
      }

      authStore.logout = props.onLogout;

      if (props.notification) {
        Notification.init(props.notification);
      }

      setLoading(false);
    },
    [
      // в ReactNativeWrapper apiUrl приходит не сразу, поэтому реагируем на появление
      // props.apiUrl,
    ],
  );

  useEffect(() => {
    if (props.lang) {
      void langStore.changeLang(props.lang as LangTypeEnum);
    }
  }, [props.lang]);

  useEffect(() => {
    void init();
  }, []);

  // Renders

  if (loading) {
    return (
      <Box position="relative" p={3} minHeight="30vh">
        <Loader isAbsolute />
      </Box>
    );
  }

  if (
    !authStore.accessToken ||
    !authStore.refreshToken ||
    requiredProps.some((prop) => props[prop as keyof MFBaseContextPropsType] === undefined)
  ) {
    console.log(t('error.requiredPropsNotFound'));

    return <MFErrorText />;
  }

  const contextValue = {
    ...props,
    // ...
  };

  return (
    <I18nextProvider i18n={i18n}>
      <MFBaseContext.Provider value={contextValue}>{props.children}</MFBaseContext.Provider>
    </I18nextProvider>
  );
});
